import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, CurrencyDollar, Users, UsersThree, TrendUp, Clock, ArrowRight } from "@phosphor-icons/react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar } from "recharts";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";

const STATUS_COLORS = { confirmed: "#2C3E35", completed: "#7A8B5C", cancelled: "#B86B5A", no_show: "#C9A227" };

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-white rounded-xl border border-[#E5E2DC] p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="label-caps">{label}</p>
          <p className="font-heading text-3xl font-semibold mt-2 tracking-tight">{value}</p>
          {sub && <p className="text-xs text-[#6B6A67] mt-1">{sub}</p>}
        </div>
        <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: `${accent}14` }}>
          <Icon size={22} weight="duotone" color={accent} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, currency } = useAuth();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const loc = lang === "es" ? "es-ES" : "en-US";

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!stats)
    return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-[#E5E2DC] border-t-[#2C3E35] animate-spin" /></div>;

  const dailyData = stats.daily_revenue.map((d) => ({ ...d, day: new Date(d.date + "T00:00:00").toLocaleDateString(loc, { weekday: "short" }) }));

  return (
    <div className="fade-up space-y-7" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-caps">{new Date().toLocaleDateString(loc, { weekday: "long", month: "long", day: "numeric" })}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight mt-1">
            {t.dash.greeting} <span className="font-semibold">{user?.name?.split(" ")[0]}</span>
          </h1>
        </div>
        <button data-testid="dash-new-appt" onClick={() => navigate("/dashboard/appointments")}
          className="px-4 py-2.5 rounded-lg bg-[#2C3E35] text-white text-sm font-medium hover:bg-[#1E2B25] transition-colors inline-flex items-center gap-2">
          {t.dash.newAppt} <ArrowRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CalendarCheck} label={t.dash.today} value={stats.today_appointments} sub={t.dash.todaySub} accent="#2C3E35" />
        <StatCard icon={CurrencyDollar} label={t.dash.week} value={money(stats.week_revenue, currency)} sub={t.dash.weekSub} accent="#7A8B5C" />
        <StatCard icon={Users} label={t.dash.customers} value={stats.total_customers} sub={t.dash.customersSub} accent="#B86B5A" />
        <StatCard icon={UsersThree} label={t.dash.team} value={stats.total_staff} sub={t.dash.teamSub} accent="#C9A227" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-xl border border-[#E5E2DC] p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="label-caps">{t.dash.revenue}</p>
              <h3 className="font-heading text-xl font-medium mt-1">{t.dash.last7} {money(stats.month_revenue, currency)} {t.dash.thisMonth}</h3>
            </div>
            <TrendUp size={22} weight="duotone" color="#7A8B5C" />
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={dailyData} margin={{ left: -18, right: 6, top: 6 }}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2C3E35" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#2C3E35" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" stroke="#6B6A67" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#6B6A67" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E2DC", fontSize: 13 }} formatter={(v) => money(v, currency)} />
              <Area type="monotone" dataKey="revenue" stroke="#2C3E35" strokeWidth={2.5} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
          <p className="label-caps mb-4">{t.dash.topServices}</p>
          {stats.service_revenue.length === 0 ? (
            <p className="text-sm text-[#6B6A67]">{t.dash.noServices}</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={stats.service_revenue} layout="vertical" margin={{ left: 0, right: 10 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={90} stroke="#6B6A67" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: "#E8E4DF55" }} contentStyle={{ borderRadius: 12, border: "1px solid #E5E2DC", fontSize: 13 }} formatter={(v) => money(v, currency)} />
                <Bar dataKey="revenue" radius={[0, 6, 6, 0]} fill="#B86B5A" barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E2DC] p-6" data-testid="upcoming-list">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock size={18} weight="duotone" color="#2C3E35" />
            <p className="label-caps">{t.dash.upcoming}</p>
          </div>
          <button onClick={() => navigate("/dashboard/calendar")} className="text-sm text-[#2C3E35] font-medium hover:text-[#B86B5A] transition-colors">{t.dash.viewCal}</button>
        </div>
        {stats.upcoming.length === 0 ? (
          <p className="text-sm text-[#6B6A67] py-6 text-center">{t.dash.noUpcoming}</p>
        ) : (
          <div className="divide-y divide-[#E5E2DC]">
            {stats.upcoming.map((a) => (
              <div key={a.id} className="flex items-center gap-4 py-3">
                <div className="w-1 h-10 rounded-full" style={{ background: a.staff_color }} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{a.customer_name}</div>
                  <div className="text-xs text-[#6B6A67]">{a.service_name} · {a.staff_name}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium">{new Date(a.start).toLocaleDateString(loc, { month: "short", day: "numeric" })}</div>
                  <div className="text-xs text-[#6B6A67]">{new Date(a.start).toLocaleTimeString(loc, { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full" style={{ background: `${STATUS_COLORS[a.status]}18`, color: STATUS_COLORS[a.status] }}>{t.st[a.status]}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
